import React, { useEffect, useState } from "react";
import { Modal, Form, ListGroup, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import { useChatContext } from "stream-chat-react";
import { useStaffChat } from "./StaffChatProvider";
import useOrgUsers from "./useOrgUsers";
import { getInitials } from "./chatUtils";

// Personal 1-1 chat: pick a person of the same organisation
const NewDirectChatModal = ({ show, onHide }) => {
  const { client, currentUserId, team } = useStaffChat();
  const { setActiveChannel } = useChatContext();
  const { users, loading, error } = useOrgUsers(show);

  const [searchText, setSearchText] = useState("");
  const [openingUserId, setOpeningUserId] = useState(null);

  useEffect(() => {
    if (show) {
      setSearchText("");
      setOpeningUserId(null);
    }
  }, [show]);

  const text = searchText.trim().toLowerCase();
  const filteredUsers = text
    ? users.filter((user) =>
        (user.name || user.id).toLowerCase().includes(text)
      )
    : users;

  const openDirectChat = async (user) => {
    if (openingUserId) {
      return;
    }
    setOpeningUserId(user.id);

    try {
      // No channel id: Stream makes it from the two members,
      // so the same two people always get the same chat.
      const channel = client.channel("messaging", {
        members: [currentUserId, user.id],
        team: team,
      });
      await channel.watch();

      setActiveChannel(channel);
      onHide();
    } catch (error) {
      console.error("Error opening chat:", error);
      toast.error("Unable to open the chat. Please try again.");
    } finally {
      setOpeningUserId(null);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title
          style={{
            fontSize: "18px",
            fontFamily: "Arial, sans-serif",
            color: "#055837",
          }}
        >
          New chat
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ fontFamily: "Arial, sans-serif" }}>
        <Form.Control
          type="text"
          placeholder="Search staff by name"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          autoFocus
          className="mb-3"
        />

        {loading && (
          <div className="text-center py-3">
            <Spinner animation="border" size="sm" /> Loading staff...
          </div>
        )}

        {!loading && error && (
          <div className="text-center text-danger py-3">{error}</div>
        )}

        {!loading && !error && filteredUsers.length === 0 && (
          <div className="text-center text-muted py-3">
            {text ? "No staff found with this name." : "No staff found."}
          </div>
        )}

        {!loading && !error && filteredUsers.length > 0 && (
          <ListGroup variant="flush" className="staff-chat-user-list">
            {filteredUsers.map((user) => (
              <ListGroup.Item
                key={user.id}
                action
                disabled={openingUserId !== null}
                onClick={() => openDirectChat(user)}
                className="d-flex align-items-center"
              >
                <span className="staff-chat-user-avatar">
                  {getInitials(user.name)}
                  {user.online && (
                    <span className="staff-chat-user-online-dot"></span>
                  )}
                </span>
                <span className="flex-grow-1 text-start">
                  {user.name || user.id}
                </span>
                {openingUserId === user.id ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <small className={user.online ? "text-success" : "text-muted"}>
                    {user.online ? "Online" : "Offline"}
                  </small>
                )}
              </ListGroup.Item>
            ))}
          </ListGroup>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default NewDirectChatModal;
